import {
  InAppModalFrequency,
  InAppSeverity,
  isInAppAnnouncementExpired,
  resolveInAppModalFrequency,
  resolveInAppSeverity,
} from './inAppNotification';

export const ADMIN_ANNOUNCEMENT_EVENT = 'admin_announcement';

export const DEFAULT_ANNOUNCEMENT_TITLE = 'Announcement';

export const ANNOUNCEMENT_DEDUPE_WINDOW_MS = 10_000;

export const MAX_ANNOUNCEMENT_QUEUE = 5;

export type AnnouncementChannel =
  | 'push_notification'
  | 'whatsapp_campaign'
  | 'email'
  | 'in_app_notification';

export interface AdminAnnouncement {
  id: string;
  channel: AnnouncementChannel | string;
  title: string;
  message: string;
  imageUrl: string | null;
  actionUrl: string | null;
  sentAt: string;
  sentByEmail: string | null;
  severity: InAppSeverity;
  expiresAt: string | null;
  frequency: InAppModalFrequency;
}

const MODAL_SEEN_STORAGE_KEY = 'wenext:announcement-modal-seen';

export const MAX_REMEMBERED_MODAL_IDS = 200;

function storageForFrequency(frequency: InAppModalFrequency): Storage | null {
  try {
    if (frequency === 'once_ever') return window.localStorage;
    if (frequency === 'once_per_session') return window.sessionStorage;
    return null;
  } catch {
    return null;
  }
}

function readSeenIds(storage: Storage): string[] {
  try {
    const raw = storage.getItem(MODAL_SEEN_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function shouldOpenModalForFrequency(
  id: string,
  frequency: InAppModalFrequency
): boolean {
  if (!id) return true;
  const storage = storageForFrequency(frequency);
  if (!storage) return true;
  return !readSeenIds(storage).includes(id);
}

export function recordModalShown(id: string, frequency: InAppModalFrequency): void {
  if (!id) return;
  const storage = storageForFrequency(frequency);
  if (!storage) return;
  try {
    const seen = readSeenIds(storage).filter((v) => v !== id);
    seen.push(id);
    const bounded = seen.slice(Math.max(0, seen.length - MAX_REMEMBERED_MODAL_IDS));
    storage.setItem(MODAL_SEEN_STORAGE_KEY, JSON.stringify(bounded));
  } catch {
  }
}

export function parseAnnouncementPayload(payload: unknown): AdminAnnouncement | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, unknown>;

  const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  const nullableStr = (v: unknown): string | null => {
    const s = str(v);
    return s ? s : null;
  };

  const title = str(raw.title);
  const message = str(raw.message);
  if (!title && !message) return null;

  const id = str(raw.id);

  return {
    id: id || `announcement-${Math.random().toString(36).slice(2)}`,
    channel: str(raw.channel) || 'push_notification',
    title,
    message,
    imageUrl: nullableStr(raw.imageUrl),
    actionUrl: nullableStr(raw.actionUrl),
    sentAt: str(raw.sentAt) || '',
    sentByEmail: nullableStr(raw.sentByEmail),
    severity: resolveInAppSeverity(raw.severity),
    expiresAt: nullableStr(raw.expiresAt),
    frequency: resolveInAppModalFrequency(raw.frequency),
  };
}

export function isAnnouncementExpired(
  announcement: Pick<AdminAnnouncement, 'expiresAt'> | null | undefined,
  now: number = Date.now()
): boolean {
  return isInAppAnnouncementExpired(announcement, now);
}

export function isAnnouncementForUser(payload: unknown, currentUserId: string | null | undefined): boolean {
  if (!currentUserId) return false;
  if (!payload || typeof payload !== 'object') return false;
  const target = (payload as Record<string, unknown>).userId;
  if (target === undefined || target === null || target === '') return true;
  return String(target) === String(currentUserId);
}

export function shouldShowAnnouncement(
  id: string,
  seen: Map<string, number>,
  now: number,
  windowMs: number = ANNOUNCEMENT_DEDUPE_WINDOW_MS
): boolean {
  const last = seen.get(id);
  if (seen.has(id) && typeof last === 'number' && now - last < windowMs) return false;
  seen.set(id, now);
  if (seen.size > 250) seen.clear();
  return true;
}

export function enqueueAnnouncement(
  queue: AdminAnnouncement[],
  next: AdminAnnouncement,
  max: number = MAX_ANNOUNCEMENT_QUEUE
): AdminAnnouncement[] {
  const deduped = [...queue.filter((a) => a.id !== next.id), next];
  if (deduped.length <= max) return deduped;
  return deduped.slice(deduped.length - max);
}

export function announcementHeading(announcement: Pick<AdminAnnouncement, 'title'>): string {
  return announcement.title || DEFAULT_ANNOUNCEMENT_TITLE;
}

export function isSafeActionUrl(url: string | null | undefined): boolean {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('//')) return false;
  if (trimmed.startsWith('/')) return true;
  return /^https?:\/\//i.test(trimmed);
}

export function isInternalActionUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.trim().startsWith('/');
}

export function inAppPopupSummary(metadata: unknown): string {
  if (!metadata || typeof metadata !== 'object') return '';
  const count = (metadata as Record<string, unknown>).inAppUserCount;
  const n = typeof count === 'number' ? count : Number(count);
  if (!Number.isFinite(n) || n <= 0) return '';
  return ` · popup shown to ${n} online user${n === 1 ? '' : 's'}`;
}
